/* Phase 3a views: Integrated Stress Tolerance Dashboard + Biofortification Index,
   plus Physiology Pipeline. Deep-teal BKAgriD theme. */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { ArrowRight, Sparkles } from 'lucide-react';

const TEAL = '#0F766E';
const TEAL_ACTIVE = '#14B8A6';
const PALETTE = ['#0F766E', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#0D9488', '#134E4A', '#0369A1', '#B45309', '#991B1B'];

const fmt = (v, n = 3) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #99F6E4' }} data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: TEAL }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="bg-teal-100 text-teal-900">
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-teal-200 text-left">{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-teal-50'}>
              {keys.map(k => {
                const v = r[k];
                const s = typeof v === 'number' ? fmt(v) : String(v ?? '-');
                return <td key={k} className="px-2 py-1 border border-teal-200 font-mono">{s}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ label, value, unit }) {
  return (
    <div className="rounded-lg p-3 bg-white border border-teal-200">
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color: TEAL }}>
        {value}{unit ? <span className="text-[10px] text-gray-500 ml-1">{unit}</span> : null}
      </div>
    </div>
  );
}

function ClassChip({ cls }) {
  const colors = {
    'Highly Tolerant': { bg: '#D1FAE5', fg: '#065F46' },
    'Tolerant': { bg: '#DBEAFE', fg: '#1E40AF' },
    'Moderate': { bg: '#FEF3C7', fg: '#92400E' },
    'Susceptible': { bg: '#FEE2E2', fg: '#991B1B' },
    'Excellent biofortified': { bg: '#D1FAE5', fg: '#065F46' },
    'Good': { bg: '#DBEAFE', fg: '#1E40AF' },
    'Low': { bg: '#FEE2E2', fg: '#991B1B' },
  };
  const c = colors[cls] || { bg: '#E5E7EB', fg: '#374151' };
  return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: c.bg, color: c.fg }}>{cls}</span>;
}

/* ============================================================
   13. INTEGRATED STRESS TOLERANCE DASHBOARD
============================================================ */
export function IntegratedToleranceView({ data }) {
  const radarData = useMemo(() => data?.top5_radar || [], [data]);
  if (!data) return null;
  const top5 = (data.composite_ranking || []).slice(0, 5).map(r => r.Genotype);
  const ranking = data.composite_ranking || [];
  const cls = data.classification_summary || {};
  return (
    <div className="space-y-4" data-testid="integrated-tolerance-view">
      <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_ACTIVE})` }}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} />
          <h3 className="font-bold">Integrated Stress Tolerance Dashboard</h3>
        </div>
        <p className="text-sm opacity-90">{data.interpretation}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatCard label="Genotypes" value={ranking.length} />
        <StatCard label="Highly Tolerant" value={cls['Highly Tolerant'] || 0} />
        <StatCard label="Tolerant" value={cls.Tolerant || 0} />
        <StatCard label="Moderate" value={cls.Moderate || 0} />
        <StatCard label="Susceptible" value={cls.Susceptible || 0} />
      </div>

      <Card title="Composite Tolerance Ranking (weighted)" testid="composite-ranking">
        <Table rows={ranking.map(r => ({ ...r, Classification: undefined }))} />
        <div className="mt-3 flex flex-wrap gap-2">
          {ranking.slice(0, 3).map(r => (
            <div key={r.Genotype} className="flex items-center gap-2 text-xs">
              <span className="font-bold">#{r.Rank} {r.Genotype}:</span>
              <ClassChip cls={r.Classification} />
              <span className="text-gray-500">score = {fmt(r.Composite_tolerance, 3)}</span>
            </div>
          ))}
        </div>
      </Card>

      {radarData.length > 0 && top5.length > 0 && (
        <Card title="Top 5 Genotypes — 4-Dimension Tolerance Profile" testid="tolerance-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="Dimension" />
              <PolarRadiusAxis angle={30} domain={[0, 1]} tick={false} />
              {top5.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="Weights Applied" testid="weights-applied">
        <Table rows={Object.entries(data.weights_used || {}).map(([k, v]) => ({ Dimension: k.replace(/_/g, ' '), Weight: v }))} />
      </Card>

      <Card title="Domains Detected in Uploaded Data" testid="domains-detected">
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(data.domains_detected || {}).map(([k, v]) => (
            <span key={k} className={`px-3 py-1 rounded-full font-semibold ${v ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-500'}`}>
              {k.charAt(0).toUpperCase() + k.slice(1)}: {v ? '✓' : '—'}
            </span>
          ))}
        </div>
      </Card>

      <div className="rounded-xl p-4 mt-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: TEAL }}>Composite Scoring Methodology</div>
        <p className="text-xs text-gray-700">
          Four normalised subscores are computed per genotype and combined as:<br />
          <code className="bg-teal-50 px-1 rounded">Composite = 0.35·Yield + 0.25·DamageTolerance + 0.15·Osmotic + 0.25·Enzyme</code><br />
          Damage markers (MDA, H₂O₂, EL) are <strong>inverted</strong> (lower is better). Enzymes (SOD/CAT/APX/POD) averaged into one defence score.
          Classification thresholds: ≥0.75 Highly Tolerant, 0.55–0.75 Tolerant, 0.35–0.55 Moderate, &lt;0.35 Susceptible.
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   14. BIOFORTIFICATION INDEX
============================================================ */
export function BiofortificationView({ data }) {
  const radarData = useMemo(() => data?.top5_radar || [], [data]);
  if (!data) return null;
  const top5 = (data.biofort_ranking || []).slice(0, 5).map(r => r.Genotype);
  const ranking = data.biofort_ranking || [];
  const cls = data.classification_summary || {};
  return (
    <div className="space-y-4" data-testid="biofort-view">
      <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_ACTIVE})` }}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} />
          <h3 className="font-bold">Biofortification Index — ICMR-Weighted Composite</h3>
        </div>
        <p className="text-sm opacity-90">{data.interpretation}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatCard label="Genotypes" value={ranking.length} />
        <StatCard label="Excellent" value={cls['Excellent biofortified'] || 0} />
        <StatCard label="Good" value={cls.Good || 0} />
        <StatCard label="Moderate" value={cls.Moderate || 0} />
        <StatCard label="Low" value={cls.Low || 0} />
      </div>

      <Card title="Biofortification Ranking (0-100)" testid="biofort-ranking">
        <Table rows={ranking.map(r => ({ ...r, Classification: undefined }))} />
        <div className="mt-3 flex flex-wrap gap-2">
          {ranking.slice(0, 3).map(r => (
            <div key={r.Genotype} className="flex items-center gap-2 text-xs">
              <span className="font-bold">#{r.Rank} {r.Genotype}:</span>
              <ClassChip cls={r.Classification} />
              <span className="text-gray-500">score = {fmt(r.Biofortification_score_0_100, 1)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Score — Bar Chart (Top 10)" testid="biofort-bar">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={ranking.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Genotype" angle={-25} textAnchor="end" height={90} fontSize={10} />
            <YAxis label={{ value: 'Biofortification score (0-100)', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Bar dataKey="Biofortification_score_0_100" fill={TEAL_ACTIVE} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {radarData.length > 0 && top5.length > 0 && (
        <Card title="Top 5 Genotypes — Nutrient Profile Radar (% of max)" testid="biofort-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="Nutrient" fontSize={10} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
              {top5.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="ICMR RDA Gap Analysis — % of Adult Daily Allowance (per 100 g serving)" testid="rda-gap">
        <Table rows={data.rda_gap_analysis || []} />
      </Card>

      <Card title="ICMR Weights Applied" testid="weights">
        <Table rows={Object.entries(data.weights_applied || {}).map(([k, v]) => ({
          Parameter: k.replace(/_/g, ' '),
          Weight: v,
          Direction: v > 0 ? 'Positive (more is better)' : 'Negative (less is better)',
        }))} />
      </Card>
    </div>
  );
}

/* ============================================================
   PHYSIOLOGY PIPELINE TOOL
============================================================ */
export function PhysiologyPipeline() {
  const navigate = useNavigate();
  const steps = [
    { n: 1, key: 'physio-photosynthesis', title: 'Gas Exchange & Chlorophyll',
      desc: 'Start with SPAD + Pn/gs/E/Ci + WUE and chlorophyll pigments — baseline photosynthetic health.' },
    { n: 2, key: 'physio-water-relations', title: 'Water Relations & Stress',
      desc: 'Measure RWC, osmotic/turgor potential, compute Fischer-Maurer SSI and Fernandez STI/GMP/HM from yields.' },
    { n: 3, key: 'physio-oxidative-stress', title: 'Oxidative Stress Markers',
      desc: 'Quantify MDA, H₂O₂, proline, protein and electrolyte leakage — cellular-damage assessment.' },
    { n: 4, key: 'physio-enzyme-activity', title: 'Enzyme Activities',
      desc: 'SOD/CAT/APX/GR/POD + antioxidant defence index + PCA of activity profiles.' },
    { n: 5, key: 'physio-integrated-tolerance', title: 'Master Physiology Report',
      desc: 'Integrated composite tolerance ranking — unified publication-ready output.' },
  ];

  const launch = (key) => navigate(`/biometrical-analysis?section=physiology&design=${key}`);

  return (
    <div className="space-y-4" data-testid="physiology-pipeline">
      <div className="rounded-xl p-4 border" style={{ background: '#F0FDFA', borderColor: '#5EEAD4' }}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={16} style={{ color: TEAL }} />
          <span className="font-display" style={{ fontSize: 15, color: '#134E4A' }}>End-to-end Stress Physiology Workflow</span>
        </div>
        <p className="text-sm" style={{ color: '#134E4A' }}>
          Run the 5 analyses in sequence. The final step pulls everything into a single integrated tolerance ranking — a ready-to-publish stress-physiology table.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {steps.map((s) => (
          <div key={s.n} className="rounded-xl p-4 bg-white flex flex-col"
            style={{ border: '1px solid #5EEAD4', boxShadow: '0 2px 8px rgba(15, 118, 110, 0.06)' }}
            data-testid={`physio-pipeline-step-${s.n}`}>
            <div className="flex items-center justify-between">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ background: TEAL_ACTIVE }}>{s.n}</span>
              {s.n < 5 && <ArrowRight size={16} style={{ color: TEAL }} />}
            </div>
            <h5 className="font-semibold text-sm mt-2 mb-1" style={{ color: TEAL }}>{s.title}</h5>
            <p className="text-[11px] text-gray-600 flex-1">{s.desc}</p>
            <button onClick={() => launch(s.key)}
              className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-full btn-press"
              style={{ background: TEAL, color: 'white' }}
              data-testid={`launch-step-${s.n}`}>
              Launch Step {s.n}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-xl p-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
        <div className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: TEAL }}>How the pipeline works</div>
        <ol className="text-xs space-y-1 list-decimal ml-5" style={{ color: '#134E4A' }}>
          <li>Upload the same genotype list to each step (or use the shared sample dataset).</li>
          <li>Each analysis stores its results under your account and can be exported individually.</li>
          <li>Step 5 — <strong>Integrated Stress Tolerance</strong> — reuses your Yield/Damage/Osmotic/Enzyme columns and outputs one composite ranking + 4-D radar.</li>
          <li>Export the master report (DOCX/PDF/XLSX) directly from the Step 5 results panel.</li>
        </ol>
      </div>
    </div>
  );
}
